export const capabilities = [
  'moderate',
  'team',
  'stats',
  'data',
  'display',
  'av',
  'events',
  'admin',
] as const;

export type Capability = (typeof capabilities)[number];
export type Capabilities = Record<Capability, boolean>;

export interface AuthenticatedUser {
  id: number;
  username: string;
  caps: Capabilities;
  name: string;
  role: string;
  org: boolean;
  orgOwner: string | null;
}

export interface EventSummary {
  id: number;
  zoom_account: number;
  event_type: 'meeting' | 'webinar';
  zoom_id: string;
  topic: string | null;
  room_name: string | null;
  status: string | null;
  active: boolean;
  auto_discovered: boolean;
  color: string;
  is_vip: boolean;
  recall_bot_id: string;
  join_url: string;
  bot_enabled: boolean;
  bot_name: string;
  team_shared: boolean;
  scheduled_at: number | null;
  created_at: number | null;
  last_seen: number | null;
}

export interface EventVerification {
  ok: boolean;
  status: string | null;
  topic: string | null;
  reason: string | null;
}

export interface ZoomConnectionAccount {
  account: number;
  configured: boolean;
  ok: boolean;
  reason: string | null;
}

export interface ZoomConnectionStatus {
  granted: boolean;
  grantedFrom: string | null;
  accounts: ZoomConnectionAccount[];
}

export interface ZoomSetupAccount {
  index: number;
  accountId: string;
  clientId: string;
  clientSecretSet: boolean;
  webhookSecretSet: boolean;
}

export interface ZoomSetupConfig {
  granted: boolean;
  grantedFrom: string | null;
  webhookPath: string;
  accounts: ZoomSetupAccount[];
}

export interface GuestAccessConfig {
  token: string;
  passcodeSet: boolean;
}

export interface RecallConfig {
  platform: boolean;
  configured: boolean;
  region: string;
  own: boolean;
  inherited: boolean;
}

export interface WhopProductConfig {
  id: string;
  label: string;
}

export interface WhopConfig {
  configured: boolean;
  keySet: boolean;
  companyId: string;
  products: WhopProductConfig[];
  statuses: string[];
}

export interface WhopProbeProduct extends WhopProductConfig {
  byStatus: Record<string, number | 'error'>;
}

export interface NotificationItem {
  id: number;
  kind: string;
  body: string;
  created_at: number;
  read_at: number | null;
  from_user_id: number | null;
  subject_user_id: number | null;
  from_first: string | null;
  from_last: string | null;
  from_name: string | null;
}

export interface CoverageRoom {
  id: number;
  name: string;
  is_vip: boolean;
  active: boolean;
}

export interface CoverageMember {
  id: number;
  name: string;
  online: boolean;
}

export interface CoverageAssignment {
  event_id: number;
  user_id: number;
  bot_name: string;
  status: string;
  has_bot: boolean;
}

export interface CoverageState {
  rooms: CoverageRoom[];
  members: CoverageMember[];
  coverage: CoverageAssignment[];
  me?: {
    id: number;
    mine: Array<{ event_id: number; bot_name: string; active: boolean }>;
  } | undefined;
}

export interface MessageThreadEntry {
  who: 'mod' | 'attendee';
  name: string | null;
  text: string;
}

export interface ModerationMessage {
  id: number;
  zoom_account: number;
  meeting_id: string;
  room_name: string | null;
  sender_name: string;
  sender_id: string;
  email: string;
  message: string;
  timestamp: number;
  flagged: boolean;
  highlighted: boolean;
  pinned: boolean;
  answered: boolean;
  deleted: boolean;
  created_at: number | null;
  reply: string | null;
  thread: MessageThreadEntry[];
  event_id?: number | undefined;
}

export interface PinQueueItem {
  pin_id: number;
  message_id: number;
  answered: boolean;
  sender_name: string;
  email: string;
  message: string;
  room_name: string | null;
  timestamp: number;
  zoom_account: number;
  highlighted: boolean;
  meeting_id: string;
}

export interface SavedSnippet {
  id: number;
  label: string;
  body: string;
  uses: number;
  created_name: string | null;
  created_by: number | null;
}

export interface SnippetLibrary {
  snippets: SavedSnippet[];
  me: number | null;
  isAdmin: boolean;
}

export interface SalesBuyer {
  id: string;
  name: string;
  product: string;
  at: number | null;
}

export interface SalesTvMessage {
  id: string;
  type: 'msg';
  text: string;
  from: string;
  at: number | null;
}

export interface SalesShoutout {
  id: string;
  type?: undefined;
  name: string;
  product: string;
  at: number | null;
  mentionedAt: number | null;
}

export type SalesMention = SalesShoutout | SalesTvMessage;

export interface SalesMentionsPayload {
  mentions: SalesMention[];
}

export interface BuyerFeed {
  configured: boolean;
  sinceAt: number | null;
  buyers: SalesBuyer[];
  max: number;
  mentioned: string[];
  messages: SalesTvMessage[];
}

export interface AnnouncementResult {
  sent: number;
  total: number;
  results: Array<{
    eventId: number;
    room: string;
    ok: boolean;
    error?: string | undefined;
  }>;
}

export const adminCapabilityKeys = [
  'can_moderate',
  'can_stats',
  'can_data',
  'can_team',
  'can_display',
  'can_av',
  'can_events',
  'is_admin',
] as const;

export type AdminCapabilityKey = (typeof adminCapabilityKeys)[number];
export type AdminCapabilityState = Record<AdminCapabilityKey, boolean>;

export interface AdminUser extends AdminCapabilityState {
  id: number;
  username: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  approved: boolean;
  zoom_owner_id: number | null;
  reset_requested: boolean;
  created_at: number | null;
}

export type AdminHealthStatus = 'ok' | 'warn' | 'fail' | 'info';

export interface AdminHealthCheck {
  key: string;
  label: string;
  status: AdminHealthStatus;
  detail: string;
}

export interface AdminHealth {
  ranAt: number;
  ok: boolean;
  checks: AdminHealthCheck[];
}

export interface AdminUsageUser {
  id: number;
  name: string;
  email: string;
  cost: number;
  hours: number;
  live: number;
  over: boolean;
  cycleStart: number;
}

export interface AdminUsage {
  limit: number;
  ratePerHour: number;
  users: AdminUsageUser[];
}

export interface AdminInvite {
  url: string;
  login: string;
  password: string;
}

export interface DataArchiveTotals {
  events: number;
  withChat: number;
  messages: number;
  replyPairs: number;
}

export interface DataArchiveEvent {
  id: number;
  room_name: string | null;
  topic: string | null;
  event_type: string | null;
  scheduled_at: number | null;
  status: string | null;
  archived: boolean;
  zoom_id: string;
  messages: number;
  first_ts: number | null;
  last_ts: number | null;
  reply_pairs: number;
}

export interface DataArchiveSummary {
  totals: DataArchiveTotals;
  events: DataArchiveEvent[];
}

export interface InsightFacts {
  messages: number;
  questions: number;
  unanswered: number;
  answered: number;
  uniquePeople: number;
  withEmail: number;
}

export interface InsightPerson {
  name: string;
  email: string | null;
  messageId: number;
  asked: string;
  answered: boolean;
  contactable: boolean;
}

export interface InsightCluster {
  label?: string | undefined;
  question?: string | undefined;
  detail?: string | undefined;
  askedTimes: number;
  variants: string[];
  people: InsightPerson[];
  unanswered: number;
}

export interface InsightReport {
  generatedAt: number;
  model: string;
  truncated: boolean;
  analysedMessages: number;
  droppedSignal: number;
  facts: InsightFacts;
  topQuestions: InsightCluster[];
  requests: InsightCluster[];
  buying: InsightCluster[];
  money: InsightCluster[];
  wants: InsightCluster[];
  important: Array<{
    question: string;
    whyItMatters: string;
    who: InsightPerson;
  }>;
  presentation: string[];
  slideDeck: string[];
  engagement: string[];
  sentiment: { overall: string; note: string } | null;
}

export type InsightStatusName = 'none' | 'building' | 'ready' | 'error';

export interface InsightStatus {
  room: string | null;
  aiReady: boolean;
  status: InsightStatusName;
  createdAt?: number | null | undefined;
  error?: string | null | undefined;
  model?: string | null | undefined;
  facts: InsightFacts;
  report: InsightReport | null;
}

export interface StatsSample {
  ts: number;
  count: number;
}

export interface StatsRoom {
  id: number;
  name: string;
  type: string | null;
  is_vip: boolean;
  color: string;
  count: number | null;
  baseline: number | null;
  delta: number | null;
  percent: number | null;
  isNew: boolean;
  peak: number | null;
  samples: StatsSample[];
  updated: number | null;
  hasBot: boolean;
  ended: boolean;
  demo: boolean;
  liveSince: number | null;
  settling: boolean;
  measureFrom: number | null;
  countSource: string | null;
  syncError: string | null;
  statsStart: number | null;
  anchored: boolean;
  pending: boolean;
}

export interface StatsMover {
  id: number;
  name: string;
  delta: number;
}

export interface StatsPayload {
  now: number;
  rooms: StatsRoom[];
  totals: {
    inRoomsNow: number;
    baseTotal: number;
    deltaTotal: number;
    peak: number;
    peakTs: number | null;
    hourStart: number | null;
    vipRooms: number;
    vipNow: number;
    biggestDrop: StatsMover | null;
    biggestGain: StatsMover | null;
  };
  demo: boolean;
  canEditStart: boolean;
}

export interface StatsSalesPayload {
  configured: boolean;
  at: number | null;
  error: string | null;
  sinceAt: number | null;
  products: Array<{
    id: string;
    label: string;
    count: number;
    delta: number;
  }>;
  total: number;
  totalDelta: number;
}

export type DisplayTheme = 'dark' | 'dark-blue' | 'blue' | 'blue-gray';
export type AvatarColorMode = 'auto' | 'room' | 'solid';
export type AiSelectivity = 'generous' | 'selective' | 'very-selective';

export interface DisplaySettings {
  chatSize: number;
  highlightSize: number;
  pinnedSize: number;
  senderSize: number;
  avatarSize: number;
  reactions: boolean;
  vipBadges: boolean;
  autoScroll: boolean;
  pinnedVisible: boolean;
  theme: DisplayTheme;
  avatars: boolean;
  avatarColorMode: AvatarColorMode;
  avatarSolidColor: string;
  aiHighlight: boolean;
  aiSelectivity: AiSelectivity;
}

export type DisplaySizeSettings = Pick<DisplaySettings, 'chatSize' | 'highlightSize' | 'pinnedSize' | 'senderSize' | 'avatarSize'>;

export interface AvPreset {
  id: number;
  name: string;
  data: DisplaySizeSettings;
}

export interface CuratedPerson {
  name?: string | undefined;
  email?: string | undefined;
  photo: string;
}

export interface CuratedPeople {
  people: CuratedPerson[];
}

export interface MessageSocketUpdate {
  id: number;
  flagged?: boolean | undefined;
  highlighted?: boolean | undefined;
  pinned?: boolean | undefined;
  answered?: boolean | undefined;
  deleted?: boolean | undefined;
  reply?: string | undefined;
}
